import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { AUCTION_STATUS_CLASS } from '../data/auctionData';
import { getAdminAuctionDetail } from '../data/mockAdapter';

export function AdminAuctionDetailPage() {
  const { facilityName, auctionId } = useParams<{ facilityName: string; auctionId: string }>();
  const decodedFacilityName = decodeURIComponent(facilityName ?? 'Unknown Facility');
  const id = auctionId ?? 'DDF-2041';
  const [auction, setAuction] = useState<Awaited<ReturnType<typeof getAdminAuctionDetail>>>(null);

  useEffect(() => {
    let isMounted = true;
    void getAdminAuctionDetail(id, decodedFacilityName).then((result) => {
      if (isMounted) {
        setAuction(result);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [decodedFacilityName, id]);

  if (!auction) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex items-center justify-between">
          <Link
            to={`/cf/${encodeURIComponent(decodedFacilityName)}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
            Back to Credit Facility
          </Link>
        </div>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">Loading auction details...</CardContent>
        </Card>
      </div>
    );
  }

  const status = AUCTION_STATUS_CLASS[auction.status];
  const reserveDeltaPct =
    auction.currentBid > 0
      ? ((auction.currentBid - auction.reservePrice) / auction.reservePrice) * 100
      : null;

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex items-center justify-between">
        <Link
          to={`/cf/${encodeURIComponent(decodedFacilityName)}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Credit Facility
        </Link>
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${status.className}`}>
          {status.label}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="flex items-center gap-2">
                <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="h-5 w-5" />
                Auction Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <span className="text-xs text-muted-foreground">Auction ID</span>
                    <p className="text-sm font-semibold mt-0.5">{auction.id}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Facility</span>
                    <p className="text-sm font-medium mt-0.5">{auction.facilityName}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Collateral</span>
                    <p className="text-lg font-bold">
                      {auction.collateralAmount} {auction.tokenSymbol}
                    </p>
                    <p className="text-sm text-muted-foreground">{auction.tokenName}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-xs text-muted-foreground">Collateral Value</span>
                    <p className="text-lg font-bold">${auction.collateralValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Debt Amount</span>
                    <p className="text-lg font-bold">${auction.debtAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Time Remaining</span>
                    <p className="flex items-center gap-2 text-lg font-bold">
                      <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-4 w-4 text-muted-foreground" />
                      {auction.timeRemaining}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Auction Timeline</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-4">
                {[...auction.timeline].reverse().map((entry, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`h-2.5 w-2.5 rounded-full flex-shrink-0 mt-1.5 ${
                          idx === 0 ? 'bg-primary' : 'bg-muted-foreground/40'
                        }`}
                      />
                      {idx < auction.timeline.length - 1 && (
                        <div className="w-px flex-1 bg-border mt-1" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 pb-4">
                      <p className="text-sm font-medium">{entry.description}</p>
                      {entry.bidder && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          By <span className="font-mono">{entry.bidder}</span>
                          {entry.amount && (
                            <span className="ml-2 font-medium">${entry.amount.toLocaleString()}</span>
                          )}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">{entry.timestamp}</span>
                        {entry.txHash && (
                          <span className="inline-flex items-center gap-1 text-xs text-primary font-mono">
                            {entry.txHash}
                            <Icon name="streamline:share-link" className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Current Bidding Status</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <span className="text-xs text-muted-foreground">Current Bid</span>
                  <p className="text-2xl font-bold mt-1">
                    {auction.currentBid > 0 ? `$${auction.currentBid.toLocaleString()}` : 'No bids'}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Total Bids</span>
                  <p className="text-2xl font-bold mt-1">{auction.bidCount}</p>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <p className="text-sm text-muted-foreground">
                  Reserve Price: <span className="font-semibold text-foreground">${auction.reservePrice.toLocaleString()}</span>
                </p>
                {reserveDeltaPct !== null && (
                  <p className={`text-sm flex items-center gap-1 ${reserveDeltaPct >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                    <Icon name="streamline:graph-bar-increase" className="h-3.5 w-3.5" />
                    {reserveDeltaPct >= 0
                      ? `${reserveDeltaPct.toFixed(1)}% above reserve price`
                      : `${Math.abs(reserveDeltaPct).toFixed(1)}% below reserve price`}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Settlement Conditions</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>&#8226; Settlement token: {auction.settlementToken}</li>
                <li>&#8226; Minimum bid increment: 1% of current bid</li>
                <li>&#8226; Winning bidder must claim collateral within 7 days</li>
                <li>&#8226; Auction extends by 10 minutes if bid placed near closing</li>
                <li>&#8226; Auction state shown here is mocked for admin review workflows</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
